import { useState } from 'react';
import { Link } from 'react-router-dom';

const SERVICES = [
  {
    id: 'furnace',
    icon: 'ri-fire-line',
    title: 'Furnace & Heating',
    tagline: 'Stay warm through every Ontario winter.',
    features: [
      'New furnace installation — all major brands',
      'Energy-efficient oil-to-gas conversions',
      'Annual maintenance & safety inspections',
      '24/7 emergency breakdown repair',
      'Thermostat upgrades & smart controls',
    ],
    detail: 'Our T.S.S.A.-certified technicians install and service gas furnaces for homes of all sizes across the GTA. Whether you need an emergency repair in January or a planned upgrade before the season, Atlantis HVAC Systems delivers expert heating solutions with transparent, upfront pricing.',
  },
  {
    id: 'ac',
    icon: 'ri-temp-cold-line',
    title: 'Air Conditioning',
    tagline: 'Precision cooling for every GTA summer.',
    features: [
      'Central air conditioning installation',
      'Spring maintenance & seasonal tune-ups',
      'Same-day AC repair — all brands',
      'Refrigerant recharge & diagnostics',
      'Energy-efficiency upgrades',
    ],
    detail: 'From new central AC installation to emergency repairs during a heatwave, Atlantis HVAC Systems keeps GTA homes cool and comfortable. We service all major brands and offer spring tune-ups to ensure peak efficiency before summer arrives.',
  },
  {
    id: 'iaq',
    icon: 'ri-wind-line',
    title: 'Indoor Air Quality',
    tagline: 'Cleaner air. Healthier home.',
    features: [
      'Lennox PureAir™ whole-home air purification',
      'HEPA filtration system installation',
      'Heat Recovery Ventilators (HRVs)',
      'Whole-home humidifiers',
      'Germicidal UV light systems',
    ],
    detail: 'Indoor air can be significantly more polluted than outdoor air. Atlantis HVAC Systems installs advanced filtration, ventilation, and purification systems — including Lennox PureAir™ and germicidal UV lights — to remove allergens, bacteria, and contaminants from your home\'s air.',
  },
  {
    id: 'duct',
    icon: 'ri-refresh-line',
    title: 'Duct Cleaning',
    tagline: 'Remove years of buildup from your system.',
    features: [
      'Whole-home duct cleaning & inspection',
      'Allergen and debris removal',
      'Improved system airflow efficiency',
      'Pre-installation system preparation',
      'Post-renovation duct cleaning',
    ],
    detail: 'Ducts accumulate dust, pet dander, mold spores, and allergens over time — reducing system efficiency and air quality. Our professional duct cleaning service removes buildup throughout your entire duct network, improving airflow and the health of your home\'s air.',
  },
];

export default function ServicesDetail() {
  const [active, setActive] = useState('furnace');
  const service = SERVICES.find((s) => s.id === active) ?? SERVICES[0];

  return (
    <section className="bg-luxury-cream py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <span className="font-caps text-[11px] tracking-[0.25em] uppercase text-luxury-text-muted block mb-4">
            Full Service Overview
          </span>
          <h2 className="font-serif text-3xl lg:text-5xl text-luxury-text-dark leading-tight">
            Explore Our <em className="not-italic text-luxury-gold-dark">Services</em>
          </h2>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {SERVICES.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`inline-flex items-center gap-2 font-caps text-xs tracking-[0.12em] uppercase px-5 py-2.5 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                active === s.id
                  ? 'bg-luxury-gold border-luxury-gold text-luxury-black'
                  : 'border-luxury-gold/25 text-luxury-text-muted hover:border-luxury-gold/50 hover:text-luxury-text-dark'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${s.icon} text-xs`} />
              </div>
              <span>{s.title}</span>
            </button>
          ))}
        </div>

        {/* Active Service */}
        <div className="bg-white rounded-3xl overflow-hidden border border-luxury-gold/10">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Info */}
            <div className="p-8 lg:p-12">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-luxury-gold/10 mb-6">
                <i className={`${service.icon} text-luxury-gold-dark text-2xl`} />
              </div>
              <h3 className="font-serif text-luxury-text-dark text-3xl font-semibold mb-2">{service.title}</h3>
              <p className="font-caps text-xs tracking-widest text-luxury-gold-dark uppercase mb-6">{service.tagline}</p>
              <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-8">{service.detail}</p>
              <div className="flex gap-3 flex-wrap">
                <a
                  href="tel:9058454009"
                  className="inline-flex items-center gap-2 bg-luxury-gold hover:bg-luxury-gold-light text-luxury-black font-caps font-semibold text-xs tracking-[0.1em] uppercase px-5 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-xs" />
                  </div>
                  <span>(905) 845-4009</span>
                </a>
                <a
                  href="tel:4168913444"
                  className="inline-flex items-center gap-2 border border-luxury-gold/30 hover:border-luxury-gold text-luxury-gold-dark font-caps text-xs tracking-[0.1em] uppercase px-5 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span>(416) 891-3444</span>
                </a>
              </div>
            </div>

            {/* Right: Features */}
            <div className="bg-luxury-warm p-8 lg:p-12 border-t lg:border-t-0 lg:border-l border-luxury-gold/10">
              <h4 className="font-caps text-xs tracking-[0.2em] uppercase text-luxury-text-muted mb-6">What&apos;s Included</h4>
              <ul className="flex flex-col gap-4">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-luxury-gold text-sm" />
                    </div>
                    <span className="font-sans text-sm text-luxury-text-dark leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-5 rounded-2xl bg-luxury-gold/8 border border-luxury-gold/15">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-gift-2-line text-luxury-gold text-sm" />
                  </div>
                  <span className="font-caps text-[10px] tracking-widest uppercase text-luxury-gold-dark whitespace-nowrap">Free Consultation</span>
                </div>
                <p className="font-sans text-xs text-luxury-text-muted leading-relaxed">
                  Get a free in-home assessment for any new equipment inquiry. No obligation, no pressure — just honest expert advice.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Post-services CTA */}
        <div className="mt-12 text-center">
          <p className="font-sans text-sm text-luxury-text-muted mb-5">Ready to get started? Speak with a specialist today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9058454009"
              className="inline-flex items-center gap-2 bg-luxury-gold hover:bg-luxury-gold-light text-luxury-black font-caps font-semibold text-xs tracking-[0.1em] uppercase px-7 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-sm" />
              </div>
              <span>GTA West: (905) 845-4009</span>
            </a>
            <a
              href="tel:4168913444"
              className="inline-flex items-center gap-2 border border-luxury-gold/40 hover:border-luxury-gold text-luxury-gold-dark font-caps font-semibold text-xs tracking-[0.1em] uppercase px-7 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              <span>GTA East: (416) 891-3444</span>
            </a>
          </div>
          <p className="mt-6 font-sans text-xs text-luxury-text-muted">
            Not sure which service you need?{' '}
            <Link to="/about" className="text-luxury-gold-dark hover:text-luxury-gold underline cursor-pointer">
              Learn about our approach
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
